// src/auth/useIdleLogout.js
import { useEffect, useRef } from "react";
import { signOut } from "firebase/auth";
import { getAuthClient } from "../firebaseConfig";

/**
 * Auto-logout after inactivity.
 * - enabled: only runs when true
 * - timeoutMs: default 5 minutes
 * - redirectTo: where to send after logout
 */
export default function useIdleLogout({
  enabled = true,
  timeoutMs = 5 * 60 * 1000,
  redirectTo = "/login",
} = {}) {
  const timer = useRef(null);

  useEffect(() => {
    if (!enabled) return;

    const auth = getAuthClient();

    const kick = async () => {
      try {
        await signOut(auth);
      } finally {
        // hard redirect to stop any lingering listeners cleanly
        window.location.replace(redirectTo);
      }
    };

    const reset = () => {
      if (document.hidden) return; // don't count hidden tab as activity
      clearTimeout(timer.current);
      timer.current = setTimeout(kick, timeoutMs);
    };

    const events = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "visibilitychange",
    ];

    events.forEach((ev) => window.addEventListener(ev, reset, { passive: true }));
    reset();

    return () => {
      clearTimeout(timer.current);
      events.forEach((ev) => window.removeEventListener(ev, reset));
    };
  }, [enabled, timeoutMs, redirectTo]);
}
